import { friendlyError } from "@/lib/friendly-errors";
import { createFileRoute, useLoaderData, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { track } from "@/lib/track";
import { useServerFn } from "@tanstack/react-start";
import { Mail, Check, AlertTriangle } from "lucide-react";
import { requestUnsubscribeLink } from "@/lib/subscribers.functions";
import { performUnsubscribeFn } from "@/lib/unsubscribe.functions";

type LoaderResult =
  | { state: "auto_success"; email: string }
  | { state: "invalid_token"; email: string }
  | { state: "needs_input" };

export const Route = createFileRoute("/unsubscribe")({
  head: () => ({
    meta: [
      { title: "Unsubscribe — Recovery Trauma™" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  validateSearch: (s: Record<string, unknown>) => ({
    email: typeof s.email === "string" ? s.email : "",
    token: typeof s.token === "string" ? s.token : "",
    bid: typeof s.bid === "string" ? s.bid : "",
  }),
  // Loader runs server-side on first GET. If the mail-client link has a valid
  // token, we unsubscribe immediately — no client-side JS required, so the
  // flow works from Gmail, Apple Mail, Outlook and every other client.
  loader: async ({ location }): Promise<LoaderResult> => {
    const search = location.search as { email?: string; token?: string; bid?: string };
    const email = (search.email ?? "").trim();
    const token = (search.token ?? "").trim();
    const bid = (search.bid ?? "").trim() || undefined;
    if (!email || !token) return { state: "needs_input" };
    const result = await performUnsubscribeFn({ data: { email, token, bid } });
    if (!result.ok) return { state: "invalid_token", email };
    return { state: "auto_success", email };
  },
  // RFC 8058 one-click: Gmail POSTs to List-Unsubscribe URL with body
  // `List-Unsubscribe=One-Click`. Handle it server-side and return 200.
  server: {
    handlers: {
      POST: async ({ request }) => {
        const url = new URL(request.url);
        const email = (url.searchParams.get("email") ?? "").trim();
        const token = (url.searchParams.get("token") ?? "").trim();
        const bid = url.searchParams.get("bid") ?? undefined;
        if (!email || !token) return new Response("Missing parameters", { status: 400 });
        const { performUnsubscribeFn: perform } = await import(
          "@/lib/unsubscribe.functions"
        );
        // Call the underlying handler by invoking the server fn directly.
        const result = await perform({ data: { email, token, bid: bid || undefined } });
        if (!result.ok) return new Response("Invalid or expired unsubscribe link", { status: 400 });
        return new Response("Unsubscribed", { status: 200 });
      },
    },
  },
  component: UnsubscribePage,
});

function UnsubscribePage() {
  const loaderData = useLoaderData({ from: "/unsubscribe" }) as LoaderResult;
  const { email: searchEmail } = useSearch({ from: "/unsubscribe" });
  const [email, setEmail] = useState(searchEmail ?? "");
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">(
    loaderData.state === "auto_success" ? "done" : "idle",
  );
  const [msg, setMsg] = useState(
    loaderData.state === "auto_success"
      ? `${loaderData.email} has been unsubscribed from all Recovery Trauma™ marketing emails.`
      : "",
  );
  const [linkSent, setLinkSent] = useState(false);

  // Record the completed opt-out (signed-link path only — a paid membership
  // is never affected by unsubscribing from marketing email).
  useEffect(() => {
    if (loaderData.state === "auto_success") {
      track("newsletter_unsubscribed", { via: "signed_link" });
    }
  }, [loaderData.state]);
  const manualUnsub = useServerFn(requestUnsubscribeLink);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      // Consent can only be changed via a signed link, so this sends a
      // confirmation email rather than unsubscribing straight away.
      await manualUnsub({ data: { email } });
      setLinkSent(true);
      setStatus("done");
      setMsg(
        `If ${email} is on our list, we've sent it a confirmation link. Open that email and tap “Confirm unsubscribe” to be removed.`,
      );
    } catch (err) {
      setStatus("error");
      setMsg(friendlyError(err, "Something went wrong. Please email support@recoverytrauma.com."));
    }
  }

  const invalidToken = loaderData.state === "invalid_token";

  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <div className="rounded-2xl border border-border bg-card p-6 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gold/15 text-[var(--gold-deep)]">
          {status === "done" ? <Check className="h-6 w-6" /> : invalidToken ? <AlertTriangle className="h-6 w-6" /> : <Mail className="h-6 w-6" />}
        </div>
        <h1 className="font-serif text-2xl text-foreground">
          {status === "done" ? (linkSent ? "Check your inbox" : "You're unsubscribed") : "Unsubscribe"}
        </h1>

        {status === "done" ? (
          <>
            <p className="mt-2 text-sm text-muted-foreground">{msg}</p>
            <p className="mt-4 text-xs text-muted-foreground">
              {linkSent
                ? "We ask for confirmation so that nobody can change your email preferences on your behalf."
                : "You will no longer receive marketing or broadcast emails from us. Essential account emails (payment receipts, security notices) may still be sent where legally required."}
            </p>
          </>
        ) : (
          <>
            {invalidToken && (
              <p className="mt-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                This unsubscribe link is invalid or has expired. Enter your email address below and
                we'll send you a fresh confirmation link.
              </p>
            )}
            {!invalidToken && (
              <p className="mt-2 text-sm text-muted-foreground">
                Enter the email address you want to remove from Recovery Trauma™ updates and we'll
                send you a confirmation link.
              </p>
            )}
            <form onSubmit={submit} className="mt-5 space-y-3 text-start">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                {status === "loading" ? "Sending link…" : "Email me an unsubscribe link"}
              </button>
              {status === "error" && <p className="text-xs text-destructive">{msg}</p>}
            </form>
            <p className="mt-4 text-xs text-muted-foreground">
              Still having trouble? Email{" "}
              <a href="mailto:support@recoverytrauma.com" className="underline">
                support@recoverytrauma.com
              </a>{" "}
              and we'll remove you manually within 24 hours.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
